import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Facebook, Twitter, Linkedin, Link2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface ShareButtonsProps {
  title: string;
  url: string;
}

const ShareButtons: React.FC<ShareButtonsProps> = ({ title, url }) => {
  const { language } = useLanguage();
  const [copied, setCopied] = React.useState(false);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(language === 'en' ? 'Link copied!' : '¡Enlace copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(language === 'en' ? 'Failed to copy' : 'Error al copiar');
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-muted-foreground">
        {language === 'en' ? 'Share this article' : 'Compartir artículo'}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="icon"
          className="hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-colors"
          onClick={() => window.open(shareLinks.facebook, '_blank', 'width=600,height=400')}
          aria-label="Share on Facebook"
        >
          <Facebook className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2] transition-colors"
          onClick={() => window.open(shareLinks.twitter, '_blank', 'width=600,height=400')}
          aria-label="Share on Twitter"
        >
          <Twitter className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors"
          onClick={() => window.open(shareLinks.linkedin, '_blank', 'width=600,height=400')}
          aria-label="Share on LinkedIn"
        >
          <Linkedin className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className={`transition-colors ${copied ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-primary/10'}`}
          onClick={copyLink}
          aria-label="Copy link"
        >
          {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default ShareButtons;
